import { useEffect, useRef, useState } from "react";
import { Box, Button, Spinner, Textarea } from "@primer/react";
import { ArrowRightIcon, ArrowUpIcon, FileIcon, PaperclipIcon, XIcon } from "@primer/octicons-react";
import { useNavigate } from "react-router-dom";
import { addDoc, collection, doc, getDocs, limit, query, Timestamp, where } from "firebase/firestore";
import { db } from "../config/firebase";
import { useAuth } from "../contexts/AuthContext";

type AttachedFile = { file: File; preview: string; base64: string };

interface AiTemplateCreatorProps {
  initialPrompt?: string;
  initialFiles?: File[];
  embedded?: boolean;
}

const suggestions = [
  "Create a professional invoice with an itemized table",
  "Draft a two-page mobile app outsourcing contract",
  "Design an A6 letter with sender and recipient details",
];

export function AiTemplateCreator({ initialPrompt = "", initialFiles = [], embedded = false }: AiTemplateCreatorProps) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [prompt, setPrompt] = useState(initialPrompt);
  const [attachedFiles, setAttachedFiles] = useState<AttachedFile[]>([]);
  const attachedFilesRef = useRef<AttachedFile[]>([]);
  const initialFilesLoaded = useRef(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [apiKey, setApiKey] = useState<string | null>(null);

  useEffect(() => { attachedFilesRef.current = attachedFiles; }, [attachedFiles]);
  useEffect(() => () => attachedFilesRef.current.forEach(({ preview }) => URL.revokeObjectURL(preview)), []);

  const prepareFiles = async (files: File[]) => Promise.all(files.map(async (file) => {
    const base64 = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => typeof reader.result === "string" ? resolve(reader.result) : reject(new Error("Unable to read image"));
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
    return { file, preview: URL.createObjectURL(file), base64 };
  }));

  useEffect(() => {
    if (initialFilesLoaded.current || !initialFiles.length) return;
    initialFilesLoaded.current = true;
    prepareFiles(initialFiles.filter((file) => file.type.startsWith("image/") && file.size <= 20 * 1024 * 1024).slice(0, 5))
      .then(setAttachedFiles)
      .catch(() => setError("Failed to process one or more images"));
  }, [initialFiles]);

  useEffect(() => {
    const loadApiKey = async () => {
      if (!user) return;
      const userRef = doc(db, "users", user.uid);
      const snapshot = await getDocs(query(collection(db, "api_keys"), where("user_ref", "==", userRef), limit(1)));
      if (!snapshot.empty) setApiKey(snapshot.docs[0].data().api_key);
    };
    loadApiKey().catch(() => setError("Unable to load your API key"));
  }, [user]);

  const addFiles = async (files: File[]) => {
    if (attachedFiles.length + files.length > 5) return setError("Maximum 5 images allowed");
    if (files.some((file) => !file.type.startsWith("image/"))) return setError("Only image files are allowed");
    if (files.some((file) => file.size > 20 * 1024 * 1024)) return setError("Image size must be less than 20MB");
    try {
      const preparedFiles = await prepareFiles(files);
      setAttachedFiles((current) => [...current, ...preparedFiles]);
      setError(null);
    } catch {
      setError("Failed to process image");
    }
  };

  const generateTemplate = async () => {
    if (!apiKey) return setError("No API key found. Please generate an API key first.");
    setLoading(true); setError(null);
    try {
      const response = await fetch("https://generatetemplate-stocfzcgjq-uc.a.run.app", {
        method: "POST",
        headers: { Authorization: `Bearer ${apiKey}`, "Content-Type": "application/json" },
        body: JSON.stringify({ prompt, imageBase64: attachedFiles[0]?.base64 }),
      });
      if (!response.ok) throw new Error(await response.text());
      const responseData = await response.json();
      const now = Timestamp.now();
      const docRef = await addDoc(collection(db, "templates"), {
        name: responseData.name || "New Template",
        html: responseData.html,
        data_json: JSON.stringify(responseData.sampleData, null, 2),
        variables: responseData.variables,
        config: { margine_bottom: 4, margine_left: 4, margine_right: 4, margine_top: 4, orientation: "Portrait", paper_size: "Letter", print_background: true },
        user_ref: doc(db, "users", user!.uid), created_at: now, updated_at: now,
      });
      navigate(`/dashboard/templates/edit/${docRef.id}`);
    } catch (generationError) {
      setError(generationError instanceof Error ? generationError.message : "Failed to generate template");
    } finally { setLoading(false); }
  };

  return (
    <Box className={embedded ? "dashboard-creator" : "ai-prompt-content"}>
      <Box className="creator-intro">
        <span className="creator-eyebrow">ForgePDF AI</span>
        <h1>{embedded ? "Create an editable HTML template" : "Create a template with AI"}</h1>
        <p>Describe the document or add a reference image. You’ll get editable HTML and sample data in the template editor.</p>
      </Box>
      <Box className="creator-composer">
        {attachedFiles.length > 0 && <Box className="creator-attachments" aria-label="Attached images">
          {attachedFiles.map((attachment, index) => <div className="creator-attachment" key={`${attachment.file.name}-${attachment.file.lastModified}`}>
            <img src={attachment.preview} alt="" /><span>{attachment.file.name}</span>
            <button type="button" aria-label={`Remove ${attachment.file.name}`} onClick={() => setAttachedFiles((files) => {
              const removed = files[index]; if (removed) URL.revokeObjectURL(removed.preview); return files.filter((_, fileIndex) => fileIndex !== index);
            })}><XIcon size={13} /></button>
          </div>)}
        </Box>}
        <Textarea value={prompt} onChange={(event) => setPrompt(event.target.value)} onPaste={(event) => {
          const files = Array.from(event.clipboardData.items).filter((item) => item.type.startsWith("image/")).map((item) => item.getAsFile()).filter((file): file is File => Boolean(file));
          if (files.length) { event.preventDefault(); addFiles(files); }
        }} placeholder="For example: a clean invoice for a design studio with a logo, customer details, line items, tax, and payment terms." rows={5} aria-label="Describe the template you want to generate" />
        <Box className="creator-composer-footer">
          <label className="creator-attach-button">
            <PaperclipIcon size={16} /> Add reference images <span>{attachedFiles.length}/5</span>
            <input type="file" accept="image/*" multiple hidden disabled={loading || attachedFiles.length >= 5} onChange={(event) => { addFiles(Array.from(event.target.files || [])); event.target.value = ""; }} />
          </label>
          <span className="creator-hint">Paste an image directly into the prompt</span>
          <Button variant="primary" className="creator-generate-button" disabled={(!prompt.trim() && !attachedFiles.length) || loading} onClick={generateTemplate} leadingVisual={loading ? Spinner : ArrowUpIcon}>{loading ? "Generating…" : "Generate template"}</Button>
        </Box>
      </Box>
      {error && <div className="creator-error" role="alert">{error}</div>}
      <Box className="creator-suggestions">
        <span>Try a starting point</span>
        {suggestions.map((suggestion) => <button type="button" key={suggestion} onClick={() => setPrompt(suggestion)}>{suggestion}<ArrowRightIcon size={14} /></button>)}
      </Box>
      {embedded && <Box className="creator-secondary-actions">
        <button type="button" onClick={() => navigate("/dashboard/templates/new")}><FileIcon size={16} /> Start a blank template</button>
      </Box>}
    </Box>
  );
}
